import appLogger from '@/utils/logger';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import Spinner from '@/components/Spinner';
import { fetchLinks } from '@/services/bots';
import { ILink } from '@/types';
import clsx from 'clsx';
import { ChangeEvent, FC, useEffect, useState } from 'react';
import FormCheckBox from './FormCheckbox';

interface IArrLink extends ILink {
  no: number;
  url: string;
  isChecked: boolean;
}

interface IAddLinkFormProps {
  setLinks: React.Dispatch<React.SetStateAction<ILink[]>>;
  links: ILink[];
  arrLinks: IArrLink[];
  setArrLinks: React.Dispatch<React.SetStateAction<IArrLink[]>>;
  onClearUrls: () => void;
}

enum SITE {
  WEBSITE,
  SITEMAP,
}

const AddLinkForm: FC<IAddLinkFormProps> = ({
  links,
  setLinks,
  arrLinks,
  setArrLinks,
  onClearUrls,
}) => {
  const [isFiltered, setIsFiltered] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [siteType, setSiteType] = useState(SITE.WEBSITE);
  const [url, setUrl] = useState('');
  // New state variables for the checkboxes
  const [isLeadGeneration, setIsLeadGeneration] = useState(false);
  const [isForCustomerSupport, setIsForCustomerSupport] = useState(false);
  const [filteredLinks, setFilteredLinks] = useState<any>([]);
  const [checkAll, setCheckAll] = useState(false);
  const numberOfPage = 50;

  const [page, setPage] = useState(0);
  const totalPages = Math.floor(links?.length / numberOfPage) + 1;
  const clearUrls = () => {
    setLinks([]);
    setArrLinks([]);
    setUrl('');
    onClearUrls(); // This will call the function passed from the parent
  };

  const handleUrlChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setUrl(newValue);

    // Check if the input includes 'sitemap.xml' anywhere in the string
    if (newValue.toLowerCase().includes('sitemap.xml')) {
      onSelectType(SITE.SITEMAP);
    } else {
      onSelectType(SITE.WEBSITE);
    }
  };

  const onNextPagination = () => {
    if (page < totalPages - 1) {
      setPage(page + 1);
    }
  };

  const onPreviewPagination = () => {
    if (page - 1 >= 0) {
      setPage(page - 1);
    }
  };

  const getLastNumber = () => {
    if (filteredLinks.length == 0) {
      if (page === totalPages - 1) {
        return links?.length;
      } else {
        return (page + 1) * numberOfPage;
      }
    } else {
      if (page === totalPages - 1) {
        return filteredLinks?.length;
      } else {
        return (page + 1) * 50;
      }
    }
  };

  // Modify the onSelectType function to accept a site type parameter
  const onSelectType = (type: SITE) => {
    if (type === SITE.SITEMAP) {
      // Clear existing links when Sitemap is selected
      setLinks([]);
      setArrLinks([]);
    }
    setSiteType(type);
  };

  const onGetLinks = async () => {
    setIsLoading(true);
    setCheckAll(false);

    // Ensure the site type is checked after state has been updated.
    if (url !== '') {
      try {
        const newLinks: ILink[] = await fetchLinks(url, siteType);
        if (newLinks && newLinks.length > 0) {
          let updatedLinks: ILink[];
          let updatedArrLinks: IArrLink[];

          if (siteType === SITE.SITEMAP) {
            updatedLinks = newLinks;
            updatedArrLinks = newLinks.map((link, index) => ({
              no: index, // Reset the numbering for sitemap links
              url: link.url,
              isChecked: false,
            }));
          } else {
            updatedLinks = [...links, ...newLinks];
            updatedArrLinks = [
              ...arrLinks,
              ...newLinks.map((link, index) => ({
                no: arrLinks.length + index,
                url: link.url,
                isChecked: false,
              })),
            ];
          }

          setLinks(updatedLinks);
          setArrLinks(updatedArrLinks);
        }
      } catch (error) {
        appLogger.error('Error fetching links:', { data: error });
      }
    }

    setIsLoading(false);
  };

  const toggle = (toggleItem: IArrLink) => {
    const arr: any = [];
    for (const link of arrLinks) {
      if (link.no === toggleItem.no && link.url === toggleItem.url) {
        arr.push({
          no: link.no,
          url: link.url,
          isChecked: !toggleItem.isChecked,
        });
      } else {
        arr.push(link);
      }
    }
    setArrLinks(arr);
  };

  const handleSearch = (value: string) => {
    value = value.trim().toLowerCase(); // Trim spaces and convert to lowercase for case insensitive comparison
    const regex = new RegExp(value, 'i'); // Use a regular expression for pattern matching

    const matched = links.filter((link) => regex.test(link.url));
    setFilteredLinks(matched);
    const arr = matched.map((link) => ({
      no: link.no,
      url: link.url,
      isChecked: arrLinks.find((al) => al.no === link.no)?.isChecked || false,
    }));
    setArrLinks(arr);
  };
  useEffect(() => {
    const arr: any = [];
    for (const link of links) {
      arr.push({
        no: link.no,
        url: link.url,
        isChecked: false,
      });
    }
    setArrLinks(arr);
  }, [links]);

  useEffect(() => {
    if (filteredLinks.length == 0) {
      const arr: any = [];
      for (const link of links) {
        arr.push({
          no: link.no,
          url: link.url,
          isChecked: checkAll,
        });
      }
      setArrLinks(arr);
    } else {
      const arr: any = [];
      for (const link of filteredLinks) {
        arr.push({
          no: link.no,
          url: link.url,
          isChecked: checkAll,
        });
      }
      setArrLinks(arr);
    }
  }, [checkAll]);

  return (
    <>
      <div className='flex flex-col items-center w-full'>
        <div className='flex flex-col mx-auto w-full'>
          <p className='text-gray-1000 text-l text-left w-full mb-5'>Choose your data type:</p>
          <div className='flex items-start gap-4'>
            <div
              className={clsx(
                'flex items-center py-3  w-full rounded-[12px] justify-center cursor-pointer transition-all',
                siteType === SITE.WEBSITE
                  ? 'border-blue-500 border-2 text-black'
                  : 'border-2 border-gray-200 hover:border-blue-500',
              )}
              onClick={() => onSelectType(SITE.WEBSITE)}
            >
              {siteType !== SITE.SITEMAP ? (
                <svg
                  width='16'
                  height='16'
                  viewBox='0 0 16 16'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <circle cx='8' cy='8' r='6' fill='white' stroke='#0066FF' strokeWidth='4' />
                </svg>
              ) : (
                <svg
                  width='16'
                  height='16'
                  viewBox='0 0 16 16'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <circle cx='8' cy='8' r='7.5' fill='white' stroke='#CED4DA' />
                </svg>
              )}
              <p className='ml-2 text-sm'>Website</p>
            </div>
            <div
              className={clsx(
                'flex items-center py-3 w-full rounded-[12px] justify-center cursor-pointer transition-all',
                siteType === SITE.SITEMAP
                  ? 'border-blue-500 border-2 text-black'
                  : 'border-2 border-gray-200 hover:border-blue-500',
              )}
              onClick={() => onSelectType(SITE.SITEMAP)}
            >
              {siteType === SITE.SITEMAP ? (
                <svg
                  width='16'
                  height='16'
                  viewBox='0 0 16 16'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <circle cx='8' cy='8' r='6' fill='white' stroke='#0066FF' strokeWidth='4' />
                </svg>
              ) : (
                <svg
                  width='16'
                  height='16'
                  viewBox='0 0 16 16'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <circle cx='8' cy='8' r='7.5' fill='white' stroke='#CED4DA' />
                </svg>
              )}

              <p className='ml-2 text-sm'>Sitemap</p>
            </div>
          </div>

          <p className='text-495057 text-13 font-inter text-xs mt-5 font-normal leading-4 mb-2 md:w-[343px] w-full'>
            Website URL
            {arrLinks.length > 0 && ( // This will check if arrLinks array has any URLs
              <button
                type='button'
                onClick={clearUrls}
                className='text-blue-500 hover:text-blue-700 transition duration-300 ease-in-out'
              >
                (Clear URLs)
              </button>
            )}
          </p>

          <div className='flex items-start gap-4'>
            <Input
              name='Website URL'
              className='md:w-[343px] w-full'
              placeholder=''
              value={url}
              onChange={handleUrlChange}
            />
            <Button className='' onClick={onGetLinks}>
              Fetch
            </Button>
          </div>
          <div className='header border-b border-gray-200 py-[10px]'>
            <Input
              name='Website URL'
              className=' w-ful my-4 border-none '
              placeholder='Filter by route patterns (e.g: /products)'
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                handleSearch(e.target.value);
              }}
            />

            <FormCheckBox
              name='checkAll'
              className={clsx('toggle-input sr-only')}
              type='checkbox'
              disabled={isFiltered} // Disable checkbox based on isFiltered state
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                // Correct typing for event
                setCheckAll(e.target.checked); // Now correctly typed, accessing 'checked' is safe
              }}
              onClick={() => {
                setCheckAll(!checkAll); // Toggle checkAll state
              }}
              value='check-all'
              label='select all'
              checked={checkAll && !isFiltered} // Uncheck automatically when filtered
            />
          </div>
          {isLoading && <Spinner className='w-full mt-6' />}
          <div className='max-h-[20vh] overflow-auto '>
            {!isLoading &&
              arrLinks.map((link: any, index: number) => {
                if (index >= page * numberOfPage && index < getLastNumber()) {
                  return (
                    <div key={index} className='px-[20px] py-[10px] '>
                      <FormCheckBox
                        name={`${link.no}`}
                        className={clsx('toggle-input sr-only ')}
                        type='checkbox'
                        onChange={(e) => {
                          toggle(link);
                        }}
                        onClick={() => {
                          toggle(link);
                        }}
                        value={`${link.no}`}
                        label={`${link.url}`}
                        checked={link.isChecked}
                      />
                    </div>
                  );
                }
              })}
          </div>

          <div
            className={clsx(
              'pagination flex justify-between items-center md:pt-5 pt-2.5 ',
              links?.length > 0 ? '' : 'invisible',
            )}
          >
            <p className='text-s md:text-base text-gray-700 px-3 py-2.5 '>
              Viewing {page * numberOfPage + 1}-{getLastNumber()} of{' '}
              {filteredLinks.length == 0 ? links?.length : filteredLinks.length} results
            </p>
            <div className='flex'>
              {page !== 0 && (
                <Button
                  className='mr-3 text-xs md:text-base'
                  variant='secondary'
                  onClick={onPreviewPagination}
                >
                  Prev
                </Button>
              )}
              {page + 1 < totalPages && (
                <Button
                  className='text-xs md:text-base'
                  variant='secondary'
                  onClick={onNextPagination}
                >
                  Next
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddLinkForm;
